import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { RegionGranularity } from "@app/region-sets";
import { isEqual } from "lodash";
import { Component } from "react";
import * as helpers from "./exportPanelBodyHelpers";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import * as toast from "./toast";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import ProjectService from "@app/projects/projectService";
import { ExportFormatType, ExportGapFilterType, AdequacyAggregateGranularity, ReportSettingsType } from "@app/exports/enumerations";
import { AccuracyInspectionSummaryAdditionalDataSection, SelectFormat } from "@app/exports/export-panel-body/filters";
import AccuracySelectComparisonRunConfigurationsFilter from "@app/exports/export-panel-body/filters/AccuracySelectComparisonRunConfigurationsFilter";
import GenerateBody from "@app/exports/export-panel-body/generate-body";
import { ReportSettingsBody, ReportSettingsSaveAction } from "@app/exports/export-panel-body/report-settings";
import { ReportSettingsProps, ReportSettingsState } from "@app/exports/export-panel-body/report-settings/reportSettings";
import { ReportPanelBodyBaseState } from "@app/exports/export-panel-body/reportPanelBodyBase";
import { ReportPanelBodyBaseProps } from "@app/exports/export-panel-body/reportPanelBodyBaseProps";
import { RegionSetFilter, SpecialtyFilter, ExportAccuracyAggregatesModel, ReportSettingsModel } from "@app/exports/exportModels";
import ExportService from "@app/exports/exportService";

interface AllInspectionsPanelBodyProps extends ReportPanelBodyBaseProps, ReportSettingsProps {
  inspectionUids: string[];
  projectId: number;
  runConfigurationId: number;
}

interface AllInspectionsPanelBodyState extends ReportPanelBodyBaseState, ReportSettingsState {
  canCompareResults: boolean;
  comparedProjectRunConfigurationId: number;
  includeInspectorResultPercentageColumns: boolean;
  includeInspectorResultExpandedOutcomes: boolean;
  isComparingResults: boolean;
  projectRunConfigurations: ProjectRunConfiguration[];
  regionSetFilter: RegionSetFilter;
  showDifferencesOnly: boolean;
  sourceProjectRunConfigurationId: number;
  specialtyFilter: SpecialtyFilter;
}

class AllInspectionsPanelBody extends Component<AllInspectionsPanelBodyProps, AllInspectionsPanelBodyState, FeatureFlagContextProps> {
  private readonly exportService = new ExportService();
  private readonly projectService = new ProjectService();
  private readonly testIdSuffix = "all-inspections";

  constructor(props: AllInspectionsPanelBodyProps & FeatureFlagContextProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
      ...this.getInitialReportSettingsState()
    };
  }

  public componentDidUpdate(prevProps: AllInspectionsPanelBodyProps) {
    if (!isEqual(prevProps.reportSettingsList, this.props.reportSettingsList)) {
      const reportSettingsList = this.props.reportSettingsList;
      if (reportSettingsList) {
        const currentReportSettings = reportSettingsList.find(rs => rs.isDefault);
        if (currentReportSettings)
          this.handleParseReportSettingsJsonToState(currentReportSettings.reportSettingsJson);
        this.setState({
          currentReportSettings,
          reportSettingsList
        })
      }
    }
  }

  public async componentDidMount() {
    const project = await this.projectService.getById(this.props.projectId, true, false, null, true);
    const projectRunConfigurations = project.projectRunConfigurations;

    this.setState((prevState) => ({
      ...prevState,
      projectRunConfigurations
    }));
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    return (
      <>
        <ReportSettingsBody
          buildReportSettingsModel={this.buildReportSettingsModel}
          createReportSettingsNameInputDisabled={this.state.createReportSettingsNameInputDisabled}
          currentReportSettings={this.state.currentReportSettings}
          isSelectionValid={this.state.isSelectionValid}
          parseReportSettingsJsonToState={this.handleParseReportSettingsJsonToState}
          reportSettingsHasChanges={this.state.reportSettingsHasChanges}
          reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
          reportSettingsList={this.state.reportSettingsList}
          saveClicked={this.state.saveClicked}
          setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
          setCurrentReportSettings={this.setCurrentReportSettings}
          setReportSettingsIsSelectionValid={this.setReportSettingsIsSelectionValid}
          setReportSettingsHasChanges={this.setReportSettingsHasChanges}
          setReportSettingsHasInvalidChanges={this.setReportSettingsHasInvalidChanges}
          setReportSettingsList={this.setReportSettingsList}
          setSaveClicked={this.setSaveClicked}
        />
        <div className="row">
          <div className="col-12 col-sm-4">
            <AccuracySelectComparisonRunConfigurationsFilter
              alwaysShowFilters={true}
              canCompareResults={this.state.canCompareResults}
              comparedProjectRunConfigId={this.state.comparedProjectRunConfigurationId}
              exportType="all-inspections"
              filterDescription="Please select a base project run option:"
              filterName="Project Run Filter"
              handleCompareToggleChange={this.handleCompareToggleChange}
              isComparingResults={this.state.isComparingResults}
              onShowDifferenceOnlyChange={this.handleShowDifferenceOnlyChange}
              onSourceRunConfigurationIdChange={this.handleSourceRunConfigurationIdChange}
              onComparedRunConfigurationIdChange={this.handleComparedRunConfigurationIdChange}
              runConfigurations={this.state.projectRunConfigurations}
              showDifferenceOnlyCheckBox={true}
              showDifferenceOnly={this.state.showDifferencesOnly}
              sourceProjectRunConfigId={this.state.sourceProjectRunConfigurationId}
            />
          </div>
          <div className="col-12 col-sm-4">
            <AccuracyInspectionSummaryAdditionalDataSection
              onShowPercentageColumnsChange={this.handleIncludePercentColumnsChange}
              onShowExpandedOutcomesChange={this.handleIncludeExpandedOutcomesChange}
              showPercentageColumns={this.state.includeInspectorResultPercentageColumns}
              showExpandedOutcomes={this.state.includeInspectorResultExpandedOutcomes}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat outputFormat={this.state.outputFormat} onOutputFormatChange={this.handleOutputFormatChange} excelDisabled={false} exportType="all-inspections" />
          </div>
        </div>
        <ReportSettingsSaveAction
          currentReportSettings={this.state.currentReportSettings}
          isSelectionValid={this.state.isSelectionValid}
          reportSettingsHasChanges={this.state.reportSettingsHasChanges}
          reportSettingsHasInvalidChanges={this.state.reportSettingsHasInvalidChanges}
          reportSettingsList={this.state.reportSettingsList}
          saveClicked={this.state.saveClicked}
          setCurrentReportSettings={this.setCurrentReportSettings}
          setCreateReportSettingsNameInputDisabled={this.setCreateReportSettingsNameInputDisabled}
          setReportSettingsHasChanges={this.setReportSettingsHasChanges}
          setReportSettingsList={this.setReportSettingsList}
          setSaveClicked={this.setSaveClicked}
          stringifyStateReportSettings={this.stringifyStateReportSettings}
        />
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={(this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved)
            && (this.state.specialtyFilter.includeFacilitySpecialties || this.state.specialtyFilter.includePhysicianSpecialties)
            && ((this.state.isComparingResults && this.state.comparedProjectRunConfigurationId !== null && this.state.comparedProjectRunConfigurationId > 0 && this.state.sourceProjectRunConfigurationId !== null && this.state.sourceProjectRunConfigurationId > 0) || !this.state.isComparingResults)}
        />
      </>
    );
  }

  private getInitialBodyState = (): AllInspectionsPanelBodyState => {
    return {
      canCompareResults: true,
      comparedProjectRunConfigurationId: null,
      emailOnSuccess: true,
      includeInspectorResultExpandedOutcomes: false,
      includeInspectorResultPercentageColumns: true,
      isComparingResults: false,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.Excel,
      projectRunConfigurations: [],
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType,
      },
      reportName: helpers.reportName(this.props.comparedRunConfigurationId !== null ? "Inspections Comparison" : "Inspections"),
      showDifferencesOnly: false,
      sourceProjectRunConfigurationId: this.props.runConfigurationId,
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as AllInspectionsPanelBodyState;
  }

  private getInitialReportSettingsState = (): ReportSettingsState => {
    return {
      createReportSettingsNameInputDisabled: true,
      currentReportSettings: undefined,
      isSelectionValid: true,
      reportSettingsHasChanges: false,
      reportSettingsHasInvalidChanges: false,
      reportSettingsList: [],
      saveClicked: false,
    } as ReportSettingsState;
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat, reportSettingsHasChanges: true });
  }

  private handleIncludePercentColumnsChange = (isChecked: boolean) => {
    this.setState({includeInspectorResultPercentageColumns: isChecked});
  }

  private handleIncludeExpandedOutcomesChange = (isChecked: boolean) => {
    this.setState({ includeInspectorResultExpandedOutcomes: isChecked });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    await this.generateExportAccuracyAggregateModel();

    toast.toastInfo(this.state.reportName);
  }

  // Accuracy
  private generateExportAccuracyAggregateModel = async () => {
    const aggregateModel: ExportAccuracyAggregatesModel = {
      aggregateGranularity: this.getAggregateGranularity(this.props.granularity),
      comparedProjectRunConfigurationId: this.state.canCompareResults && this.state.isComparingResults ? this.state.comparedProjectRunConfigurationId : null,
      countyFilters: this.props.countyFilters,
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeExpandedOutcomes: this.state.includeInspectorResultExpandedOutcomes,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includeInspectorResultPercentageColumns: this.state.includeInspectorResultPercentageColumns,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      inspectionUids: this.props.inspectionUids,
      networkAssessmentAnalysisType: this.state.regionSetFilter.networkAssessmentAnalysisType,
      projectRunConfigurationId: this.state.sourceProjectRunConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      runAsynchronous: true,
      showDifferencesOnly: this.state.showDifferencesOnly,
      specialtyGapFilter: ExportGapFilterType.All,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
      stateFilters: this.props.stateFilters,
    };
    await this.exportService.postExportAccuracyAggregates(aggregateModel);
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess, reportSettingsHasChanges: true });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {

    let physiciansSelected: boolean = this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean = this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  }

  private handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  }

  private getAggregateGranularity = (granularity: RegionGranularity) => {
    switch (granularity) {
      case RegionGranularity.County:
        if (this.props.regionSetIds.length > 0) { // if any counties are selected, report at county granularity otherwise report at state granularity
          return AdequacyAggregateGranularity.County;
        } else {
          return AdequacyAggregateGranularity.State;
        }
      case RegionGranularity.State:
        if (this.props.regionSetIds.length > 0) { // if any states are selected, report at state granularity otherwise report at service area granularity
          return AdequacyAggregateGranularity.State;
        } else {
          return AdequacyAggregateGranularity.ServiceArea;
        }
      default:
        throw new Error(`No translation for RegionGranularity: '${granularity}' to AdequacyAggregateGranularity`);
    }
  }

  // Report Settings
  private stringifyStateReportSettings = (): string => {
    return helpers.stringifyStateReportSettings({
      emailOnSuccess: this.state.emailOnSuccess,
      loadingReportSettingsJSONStrings: this.state.loadingReportSettingsJSONStrings,
      outputFormat: this.state.outputFormat,
      regionSetFilter: this.state.regionSetFilter,
      specialtyFilter: this.state.specialtyFilter,
    } as AllInspectionsPanelBodyState);
  }

  private buildReportSettingsModel = (isDefault: boolean, name: string): ReportSettingsModel => {
    const reportSettingsJson = this.stringifyStateReportSettings();
    return {
      isDefault,
      name,
      reportSettingsJson,
      type: this.reportSettingsType,
    } as ReportSettingsModel;
  }

  private get reportSettingsType() {
    switch (this.props.match.params.module) {
      case "accuracy":
        return ReportSettingsType.AccuracyAllInspections;
      default:
        return ReportSettingsType.Unknown;
    }
  }

  private handleParseReportSettingsJsonToState = (reportSettingsJson: string) => {
    const settings = helpers.tryParseReportSettings(reportSettingsJson);
    if (settings) {
      this.setReportSettingsIsSelectionValid(true);
      this.setState((prevState) => ({
        ...prevState,
        ...settings,
      }));
    }
    else {
      const initialBodyState = this.getInitialBodyState();
      this.setReportSettingsIsSelectionValid(false);
      this.setState((prevState) => ({
        ...prevState,
        ...initialBodyState
      }));
    }
  }

  private setCurrentReportSettings = (currentReportSettings: ReportSettingsModel) => {
    this.setState((prevState) => ({
      ...prevState,
      currentReportSettings,
    }));
  }

  private setReportSettingsList = (rsList: ReportSettingsModel[]) => {
    const reportSettingsList = this.props.sortedReportSettings(rsList, "name");
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsList,
    }))
  }

  private setReportSettingsHasChanges = (reportSettingsHasChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges,
    }))
  }

  private setReportSettingsHasInvalidChanges = (reportSettingsHasInvalidChanges: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasInvalidChanges,
    }))
  }

  private setReportSettingsIsSelectionValid = (isSelectionValid: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      isSelectionValid,
    }))
  }

  private setCreateReportSettingsNameInputDisabled = (createReportSettingsNameInputDisabled: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      createReportSettingsNameInputDisabled,
    }))
  }

  private setSaveClicked = (saveClicked: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      saveClicked,
    }))
  }

  private handleCompareToggleChange = (active: boolean) => {
    const newName = this.state.isComparingResults ? helpers.reportName("Inspections") : helpers.reportName("Inspections Comparison");
    this.setState((prevState) => ({
      ...prevState,
      isComparingResults: active,
      reportName: newName,
      reportSettingsHasChanges: true,
    }));
  }

  private handleSourceRunConfigurationIdChange = (sourceRunConfigId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      reportSettingsHasChanges: true,
      sourceProjectRunConfigurationId: sourceRunConfigId
    }));
  }

  private handleComparedRunConfigurationIdChange = (comparedRunConfigId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      comparedProjectRunConfigurationId: comparedRunConfigId,
      reportSettingsHasChanges: true,
    }));
  }

  private handleShowDifferenceOnlyChange = (check: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      showDifferencesOnly: check
    }));
  }
}

const WrappedAllInspectionsPanelBody = withFeatureFlagContext(AllInspectionsPanelBody);

export default WrappedAllInspectionsPanelBody;
